import React from 'react';
import moment from 'moment';
import { Pie, Bar, Line, Scatter } from 'react-chartjs-2';
import {
    Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale,
    LinearScale, BarElement, PointElement,
    LineElement, Title,
} from 'chart.js';
import { formatValueWithType } from '../../../utils/utility';

ChartJS.register(ArcElement, Title, Tooltip, Legend, CategoryScale, LinearScale, BarElement, PointElement, LineElement);

const Charts = ({ data }) => {
    const { successCount, failureCount, incompleteTradeCount, totalTrades,
        holdingDaysGroupedProfit = [], holdingDaysGroupedLoss = [], holdingDaysGroupedIncomplete = [],
        successWithAverage, failureWithAverage, inCompleteWithAverage,
        groupedByYear = {}, groupedByMonth = {}, openTradeList = [], maxHoldingByDay = [],
        maxHoldingAmountByDay = [], minimumInitialCashRequired = 0,
        completedTradeList = [], meta = {}
    } = data;
    const detailedTradeList = [...completedTradeList, ...openTradeList];
    const { company = {} } = meta;
    const successPercentage = formatValueWithType((successCount / totalTrades) * 100);
    const failurePercentage = formatValueWithType((failureCount / totalTrades) * 100);
    const incompleteTradePrecentage = formatValueWithType((incompleteTradeCount / totalTrades) * 100);


    const pieData = {
        labels: ['Success', 'Failure', 'Incomplete'],
        datasets: [
            {
                data: [successPercentage, failurePercentage, incompleteTradePrecentage],
                backgroundColor: ['#28a745', '#dc3545', '#758895'],
            },
        ],
    };
    const chartCardStyle = {
        background: '#ffffff',
        border: '1px solid #e9ecef',
        borderRadius: '12px',
        boxShadow: '0 6px 18px rgba(15, 23, 42, 0.08)',
        padding: '14px',
        marginBottom: '14px',
    };

    const renderChartCard = (chart) => (
        <div style={chartCardStyle}>
            {chart}
        </div>
    );


    const getProfitTrend = () => {
        if (completedTradeList.length === 0)
            return '';
        const options = {
            responsive: true,
            plugins: {
                legend: {
                    position: 'top',
                },
                title: {
                    display: true,
                    text: 'Profit Percentage Trend',
                },
            },
        };
        const labels = completedTradeList.map(trade => company[trade.companyId]?.name || trade.name);
        const data = {
            labels,
            datasets: [
                {
                    fill: true,
                    label: 'Profit %',
                    data: completedTradeList.map((trade) => trade.netProfitPercentage),
                    borderColor: 'rgb(53, 162, 235)',
                    backgroundColor: 'rgba(53, 162, 235, 0.5)',
                },
            ],
        };

        return <Line options={options} data={data} />
    }

    const getQuantityTrend = () => {
        if (completedTradeList.length === 0)
            return '';
        const options = {
            responsive: true,
            plugins: {
                legend: {
                    position: 'top',
                },
                title: {
                    display: true,
                    text: 'Quantity Trend',
                },
            },
        };
        const labels = completedTradeList.map(trade => company[trade.companyId]?.name || trade.name);
        const data = {
            labels,
            datasets: [
                {
                    fill: true,
                    label: 'Quantity',
                    data: completedTradeList.map((trade) => trade.quantity),
                    borderColor: '#046f3d',
                    backgroundColor: '#28b674',
                },
            ],
        };

        return <Line options={options} data={data} />
    }

    const getHoldingDaysGrouped = () => {
        const options = {
            indexAxis: 'y',
            elements: {
                bar: {
                    borderWidth: 2,
                },
            },
            responsive: true,
            plugins: {
                legend: {
                    position: 'right',
                },
                title: {
                    display: true,
                    text: 'Holding Days Grouped (Includes non-trading)',
                },
            },
            scales: {
                x: {
                    beginAtZero: false, // Start the y-axis from 0
                    ticks: {
                        stepSize: 10, // Set the step size for the y-axis
                    },
                    title: {
                        display: true,
                        text: 'Trade Count', // Label for x-axis
                    },
                },
                y: {
                    title: {
                        display: true,
                        text: 'Days', // Label for y-axis
                    },
                },
            },
        };
        const labels = holdingDaysGroupedProfit.map(session => session.range);
        const profitHoldingDaysMap = holdingDaysGroupedProfit.reduce((accum, session) => {
            return { ...accum, [session.range]: session.count };
        }, {});
        const lossHoldingDaysMap = holdingDaysGroupedLoss.reduce((accum, session) => {
            return { ...accum, [session.range]: session.count };
        }, {});
        const incompleteHoldingDaysMap = holdingDaysGroupedIncomplete.reduce((accum, session) => {
            return { ...accum, [session.range]: session.count };
        }, {});
        const completedProfitCount = holdingDaysGroupedProfit.reduce((sum, session) => sum + (session.count || 0), 0);
        const completedLossCount = holdingDaysGroupedLoss.reduce((sum, session) => sum + (session.count || 0), 0);
        const incompleteCount = holdingDaysGroupedIncomplete.reduce((sum, session) => sum + (session.count || 0), 0);
        const datasets = [
            {
                label: `Completed With Profit (${completedProfitCount})`,
                data: labels.map((range) => profitHoldingDaysMap[range] || 0),
                borderColor: '#28a745',
                backgroundColor: '#28a745',
            },
            {
                label: `Completed With Loss (${completedLossCount})`,
                data: labels.map((range) => lossHoldingDaysMap[range] || 0),
                borderColor: '#dc3545',
                backgroundColor: '#dc3545',
            },
            {
                label: `Incomplete Trades (${incompleteCount})`,
                data: labels.map((range) => incompleteHoldingDaysMap[range] || 0),
                borderColor: '#758895',
                backgroundColor: '#758895',
            },
        ];
        const data = {
            labels,
            datasets,
        };

        return <Bar options={options} data={data} />;
    }

    const getDipDuringTrade = () => {
        if (completedTradeList.length === 0)
            return '';
        const names = completedTradeList.map((trade) => company[trade.companyId]?.name || trade.name);
        const dipDuringTrade = completedTradeList.map((trade) => trade.lowestPriceDuringTrade ? formatValueWithType(((trade.lowestPriceDuringTrade - trade.buyPrices[0]) / trade.buyPrices[0]) * 100) : 0);
        const chartOptions = {
            responsive: true,
            plugins: {
                legend: { display: false },
                tooltip: { enabled: true },
                title: {
                    display: true,
                    text: 'Dip During Trade Percentage',
                },
            },
            scales: {
                x: { title: { display: true, text: 'Trades' } },
                y: { title: { display: true, text: 'Dip (%)' } },
            },
        };

        return <Bar
            data={{
                labels: names,
                datasets: [
                    {
                        label: 'Dip (%)',
                        data: dipDuringTrade,
                        backgroundColor: 'orange',
                    },
                ],
            }}
            options={chartOptions}
        />
    }

    const getHoldingDays = () => {
        if (completedTradeList.length === 0)
            return '';
        const names = completedTradeList.map((trade) => company[trade.companyId]?.name || trade.name);
        const holdingDays = completedTradeList.map((trade) => trade.holdingDays);
        const chartOptions = {
            responsive: true,
            plugins: {
                legend: { display: false },
                tooltip: { enabled: true },
                title: {
                    display: true,
                    text: 'Holding Days (Includes non-trading)',
                },
            },
            scales: {
                x: { title: { display: true, text: 'Trades' } },
                y: { title: { display: true, text: 'Holding Day count' } },
            },
        };

        return <Bar
            data={{
                labels: names,
                datasets: [
                    {
                        label: 'Days',
                        data: holdingDays,
                        backgroundColor: 'blue',
                    },
                ],
            }}
            options={chartOptions}
        />
    }

    const getAveragedData = (key = '') => {
        const dataMapping = {
            'SUCCESS': {
                label: 'Success',
                data: [successCount, successWithAverage]
            },
            'FAILURE': {
                label: 'Failure',
                data: [failureCount, failureWithAverage]
            },
            'IN_COMPLETE': {
                label: 'Incomplete',
                data: [incompleteTradeCount, inCompleteWithAverage]
            }
        }
        const options = {
            plugins: {
                legend: {
                    position: 'top',
                },
                title: {
                    display: true,
                    text: `Averaged Data - ${dataMapping[key].label}`,
                },
            },
            responsive: true,
            interaction: {
                mode: 'index',
                intersect: false,
            },
            scales: {
                x: {
                    stacked: true,
                },
                y: {
                    stacked: true,
                },
            },
        };
        const labels = [dataMapping[key].label];
        const data = {
            labels,
            datasets: [
                {
                    label: 'Total Trades',
                    data: [dataMapping[key].data[0]],
                    backgroundColor: 'rgba(234, 55, 0, 0.5)',
                    stack: 'Stack 0',
                },
                {
                    label: 'Non-Averaged Trades',
                    data: [dataMapping[key].data[0] - dataMapping[key].data[1]],
                    backgroundColor: 'rgba(50, 245, 131, 0.5)',
                    stack: 'Stack 1',
                },
                {
                    label: 'Averaged Trades',
                    data: [dataMapping[key].data[1]],
                    backgroundColor: 'rgba(241, 18, 11, 0.5)',
                    stack: 'Stack 1',
                },
            ],
        };

        return <Bar options={options} data={data} />
    }

    const getGroupByDate = (key = 'YEAR') => {
        const mapping = {
            'YEAR': groupedByYear,
            'MONTH': groupedByMonth
        }
        const momentMapper = {
            'YEAR': 'YYYY',
            'MONTH': 'MM-YYYY'
        }
        const labelMapper = {
            'YEAR': 'Year',
            'MONTH': 'month'
        }

        let keys = Object.keys(mapping[key]);
        const format = momentMapper[key];
        const direction = 'asc';
        keys.sort((a, b) => {
            const aDate = moment(a, format);
            const bDate = moment(b, format);
            if (!aDate.isValid() && !bDate.isValid()) return 0;
            if (!aDate.isValid()) return direction === 'asc' ? 1 : -1;
            if (!bDate.isValid()) return direction === 'asc' ? -1 : 1;

            return direction === 'asc' ? aDate - bDate : bDate - aDate;
        })

        const line_options = {
            responsive: true,
            interaction: {
                mode: 'index',
                intersect: false,
            },
            plugins: {
                legend: {
                    position: 'top',
                },
                title: {
                    display: true,
                    text: `Trades by ${labelMapper[key]}`,
                },
            },
        };
        const line_labels = keys;
        const line_data = {
            labels: line_labels,
            datasets: [
                {
                    label: 'Completed With Average',
                    data: line_labels.map((k, index) => (mapping[key][k].completedWithAverage)),
                    borderColor: 'rgb(99, 234, 255)',
                    backgroundColor: 'rgba(66, 150, 174, 0.5)',
                },
                {
                    label: 'Completed Without Average',
                    data: line_labels.map((k, index) => (mapping[key][k].completedWithoutAverage)),
                    borderColor: 'rgba(77, 238, 144, 0.92)',
                    backgroundColor: 'rgba(50, 245, 131, 0.5)',
                },
                {
                    label: 'In-Completed With Average',
                    data: line_labels.map((k, index) => (mapping[key][k].incompleteWithAverage)),
                    borderColor: 'rgb(53, 162, 235)',
                    backgroundColor: 'rgba(53, 162, 235, 0.5)',
                },
                {
                    label: 'In-Completed Without Average',
                    data: line_labels.map((k, index) => (mapping[key][k].incompleteWithoutAverage)),
                    borderColor: 'rgb(255, 99, 132)',
                    backgroundColor: 'rgba(255, 99, 132, 0.5)',
                },
            ],
        };

        return <Line options={line_options} data={line_data} />;
    }

    const getInCompleteGroupByDate = (key = 'YEAR', labelStart = 'Holdings by', data, label = 'Max holdings') => {
        const labelMapper = {
            'YEAR': 'Year',
            'MONTH': 'month',
            'DAY': 'Days'
        }
        const line_options = {
            responsive: true,
            interaction: {
                mode: 'index',
                intersect: false,
            },
            plugins: {
                legend: {
                    position: 'top',
                },
                title: {
                    display: true,
                    text: `${labelStart} ${labelMapper[key]}`,
                },
            },
        };
        const line_labels = data.map(datum => datum.key);
        const line_data = {
            labels: line_labels,
            datasets: [
                {
                    label,
                    data: data.map((k, index) => k.value),
                    borderColor: 'rgb(99, 234, 255)',
                    backgroundColor: 'rgba(66, 150, 174, 0.5)',
                }
            ],
        };

        return <Line options={line_options} data={line_data} />;
    }

    const getOpenTrades = () => {
        if (openTradeList.length === 0) {
            return '';
        }
        const names = openTradeList.map((trade) => company[trade.companyId]?.name || trade.name);
        const percentageValues = openTradeList.map((trade) => {
            const { lastClosePrice = 0, avgBuyPrice = 0 } = trade;
            return formatValueWithType(((lastClosePrice - avgBuyPrice) / avgBuyPrice) * 100);
        });

        const chartOptions = {
            responsive: true,
            plugins: {
                legend: { display: false },
                tooltip: { enabled: true },
                title: {
                    display: true,
                    text: 'Open Trades current status',
                },
            },
            scales: {
                x: { title: { display: true, text: 'Trades' } },
                y: { title: { display: true, text: 'Change (%)' } },
            },
        };

        return <Bar
            data={{
                labels: names,
                datasets: [
                    {
                        label: 'Change (%)',
                        data: percentageValues,
                        backgroundColor: 'purple',
                    },
                ],
            }}
            options={chartOptions}
        />
    }

    const getNumeric = (value, fallback = 0) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : fallback;
    };

    const getCapitalGrowth = () => {
        if (!detailedTradeList.length) return '';

        const events = [];
        const lastKnownPrice = {};

        detailedTradeList.forEach((trade) => {
            const symbol = company[trade.companyId]?.additionalInfo?.nseCode || company[trade.companyId]?.name || 'UNKNOWN';
            const quantity = getNumeric(trade.quantity, 1);

            if (getNumeric(trade.lastClosePrice) > 0) {
                lastKnownPrice[symbol] = getNumeric(trade.lastClosePrice);
            }

            if (
                Array.isArray(trade.buyDates) &&
                Array.isArray(trade.buyPrices) &&
                Array.isArray(trade.buyQties) &&
                trade.buyDates.length === trade.buyPrices.length &&
                trade.buyPrices.length === trade.buyQties.length &&
                trade.buyDates.length > 0
            ) {
                trade.buyDates.forEach((date, idx) => {
                    if (!moment(date, 'YYYY-MM-DD', true).isValid()) return;
                    const price = getNumeric(trade.buyPrices[idx]);
                    const qty = getNumeric(trade.buyQties[idx], 1);
                    if (price > 0 && qty > 0) events.push({ date, type: 'BUY', symbol, price, qty });
                });
            } else if (moment(trade.buyDate, 'YYYY-MM-DD', true).isValid()) {
                const price = getNumeric(trade.buyPrice);
                if (price > 0 && quantity > 0) events.push({ date: trade.buyDate, type: 'BUY', symbol, price, qty: quantity });
            }

            if (
                Array.isArray(trade.sellDates) &&
                Array.isArray(trade.sellPrices) &&
                Array.isArray(trade.sellQties) &&
                trade.sellDates.length === trade.sellPrices.length &&
                trade.sellPrices.length === trade.sellQties.length &&
                trade.sellDates.length > 0
            ) {
                trade.sellDates.forEach((date, idx) => {
                    if (!moment(date, 'YYYY-MM-DD', true).isValid()) return;
                    const price = getNumeric(trade.sellPrices[idx]);
                    const qty = getNumeric(trade.sellQties[idx], 1);
                    if (price > 0 && qty > 0) events.push({ date, type: 'SELL', symbol, price, qty });
                });
            } else if (moment(trade.sellDate, 'YYYY-MM-DD', true).isValid()) {
                const price = getNumeric(trade.sellPrice);
                if (price > 0 && quantity > 0) events.push({ date: trade.sellDate, type: 'SELL', symbol, price, qty: quantity });
            }
        });

        if (!events.length) return '';

        events.sort((a, b) => {
            const aDate = moment(a.date, 'YYYY-MM-DD');
            const bDate = moment(b.date, 'YYYY-MM-DD');
            if (aDate.isBefore(bDate)) return -1;
            if (aDate.isAfter(bDate)) return 1;
            if (a.type === b.type) return 0;
            return a.type === 'SELL' ? -1 : 1;
        });

        const positions = {};
        let cash = getNumeric(minimumInitialCashRequired);
        const daySnapshot = {};

        events.forEach((event) => {
            const notional = event.price * event.qty;
            if (event.type === 'BUY') {
                cash -= notional;
                positions[event.symbol] = (positions[event.symbol] || 0) + event.qty;
            } else {
                cash += notional;
                positions[event.symbol] = Math.max(0, (positions[event.symbol] || 0) - event.qty);
            }
            lastKnownPrice[event.symbol] = event.price;

            const stockValue = Object.keys(positions).reduce((sum, symbol) => {
                const qty = getNumeric(positions[symbol]);
                if (qty <= 0) return sum;
                return sum + (qty * getNumeric(lastKnownPrice[symbol]));
            }, 0);

            daySnapshot[event.date] = {
                cash: formatValueWithType(cash),
                stock: formatValueWithType(stockValue),
                total: formatValueWithType(cash + stockValue),
            };
        });

        const labels = Object.keys(daySnapshot).sort((a, b) => moment(a, 'YYYY-MM-DD') - moment(b, 'YYYY-MM-DD'));
        if (!labels.length) return '';

        return <Line
            options={{
                responsive: true,
                plugins: {
                    title: { display: true, text: 'Capital Growth Over Time (Cash + Stock)' },
                    tooltip: { enabled: true },
                },
                scales: {
                    x: { title: { display: true, text: 'Date' } },
                    y: { title: { display: true, text: 'Capital Value' } },
                },
            }}
            data={{
                labels,
                datasets: [
                    {
                        label: 'Total Capital',
                        data: labels.map((d) => daySnapshot[d].total),
                        borderColor: '#17a2b8',
                        backgroundColor: 'rgba(23, 162, 184, 0.25)',
                        fill: true,
                    },
                    {
                        label: 'Cash',
                        data: labels.map((d) => daySnapshot[d].cash),
                        borderColor: '#28a745',
                        backgroundColor: 'rgba(40, 167, 69, 0.15)',
                        fill: false,
                    },
                    {
                        label: 'Stock Value',
                        data: labels.map((d) => daySnapshot[d].stock),
                        borderColor: '#fd7e14',
                        backgroundColor: 'rgba(253, 126, 20, 0.15)',
                        fill: false,
                    },
                ],
            }}
        />;
    };

    const getSectorIndustryPerformance = () => {
        if (!completedTradeList.length) return '';

        const buildAggregates = (key) => {
            const map = {};
            completedTradeList.forEach((trade) => {
                const { companyId = '' } = trade;
                const list = Array.isArray(company[companyId]?.additionalInfo?.[key]) && company[companyId]?.additionalInfo?.[key].length ? company[companyId]?.additionalInfo?.[key] : [{ name: 'Unknown' }];
                list.forEach((entry) => {
                    const name = entry.name || 'Unknown';
                    if (!map[name]) map[name] = { sumProfit: 0, count: 0 };
                    map[name].sumProfit += getNumeric(trade.netProfitPercentage);
                    map[name].count += 1;
                });
            });
            return Object.entries(map)
                .map(([name, values]) => ({ name, avgProfit: formatValueWithType(values.sumProfit / values.count), count: values.count }))
                .sort((a, b) => b.count - a.count)
                .slice(0, 10);
        };

        const sector = buildAggregates('sector');
        const industry = buildAggregates('industry');
        const labels = [...new Set([...sector.map((i) => `Sector: ${i.name}`), ...industry.map((i) => `Industry: ${i.name}`)])];
        const byLabel = (arr, key) => labels.map((label) => (arr.find((x) => `${key}: ${x.name}` === label) || {}));
        const sectorRows = byLabel(sector, 'Sector');
        const industryRows = byLabel(industry, 'Industry');

        return <Bar
            options={{
                responsive: true,
                plugins: {
                    title: { display: true, text: 'Sector / Industry Performance (Avg Profit% and Trade Count)' },
                    tooltip: { enabled: true },
                },
                scales: {
                    y: { title: { display: true, text: 'Avg Profit %' } },
                    y1: { position: 'right', grid: { drawOnChartArea: false }, title: { display: true, text: 'Trade Count' } },
                    x: { title: { display: true, text: 'Category' } },
                },
            }}
            data={{
                labels,
                datasets: [
                    {
                        label: 'Avg Profit %',
                        data: labels.map((_, idx) => formatValueWithType((getNumeric(sectorRows[idx]?.avgProfit) + getNumeric(industryRows[idx]?.avgProfit)) || 0)),
                        backgroundColor: 'rgba(40, 167, 69, 0.6)',
                        yAxisID: 'y',
                    },
                    {
                        label: 'Trade Count',
                        data: labels.map((_, idx) => (getNumeric(sectorRows[idx]?.count) + getNumeric(industryRows[idx]?.count))),
                        backgroundColor: 'rgba(53, 162, 235, 0.6)',
                        yAxisID: 'y1',
                    },
                ],
            }}
        />;
    };

    const getSymbolWiseWinRate = () => {
        if (!detailedTradeList.length) return '';
        const mapping = {};
        detailedTradeList.forEach((trade) => {
            const key = company[trade.companyId]?.additionalInfo?.nseCode || company[trade.companyId]?.name || 'UNKNOWN';
            if (!mapping[key]) mapping[key] = { win: 0, loss: 0, open: 0 };
            if (!trade.tradeCompleted) mapping[key].open += 1;
            else if (getNumeric(trade.netProfitPercentage, getNumeric(trade.netProfit)) >= 0) mapping[key].win += 1;
            else mapping[key].loss += 1;
        });
        const labels = Object.keys(mapping).slice(0, 20);
        return <Bar
            options={{
                responsive: true,
                plugins: { title: { display: true, text: 'Symbol-wise Trade Outcome (Win / Loss / Open)' } },
                scales: { x: { stacked: true, title: { display: true, text: 'Symbol' } }, y: { stacked: true, title: { display: true, text: 'Trade Count' } } },
            }}
            data={{
                labels,
                datasets: [
                    { label: 'Wins', data: labels.map((k) => mapping[k].win), backgroundColor: '#28a745' },
                    { label: 'Losses', data: labels.map((k) => mapping[k].loss), backgroundColor: '#dc3545' },
                    { label: 'Open', data: labels.map((k) => mapping[k].open), backgroundColor: '#758895' },
                ],
            }}
        />;
    };

    const getStopLossImpact = () => {
        if (!completedTradeList.length) return '';
        const group = { hit: { count: 0, wins: 0, sum: 0 }, notHit: { count: 0, wins: 0, sum: 0 } };
        completedTradeList.forEach((trade) => {
            const key = trade.isStopLossHit ? 'hit' : 'notHit';
            const pnl = getNumeric(trade.netProfitPercentage);
            group[key].count += 1;
            group[key].sum += pnl;
            if (pnl >= 0) group[key].wins += 1;
        });
        const labels = ['SL Hit', 'SL Not Hit'];
        const avgReturn = [group.hit, group.notHit].map((g) => g.count ? formatValueWithType(g.sum / g.count) : 0);
        const winRate = [group.hit, group.notHit].map((g) => g.count ? formatValueWithType((g.wins / g.count) * 100) : 0);
        return <Bar
            options={{
                responsive: true,
                plugins: { title: { display: true, text: 'Stop-loss Impact (Avg Return% and Win Rate%)' } },
                scales: {
                    y: { title: { display: true, text: 'Avg Return %' } },
                    y1: { position: 'right', grid: { drawOnChartArea: false }, title: { display: true, text: 'Win Rate %' } },
                },
            }}
            data={{
                labels,
                datasets: [
                    { label: 'Avg Return %', data: avgReturn, backgroundColor: 'rgba(255, 159, 64, 0.7)', yAxisID: 'y' },
                    { label: 'Win Rate %', data: winRate, backgroundColor: 'rgba(75, 192, 192, 0.7)', yAxisID: 'y1' },
                ],
            }}
        />;
    };

    const getAveragingEffectiveness = () => {
        if (!completedTradeList.length) return '';
        const groups = {
            Averaged: { count: 0, wins: 0, profitSum: 0, dipSum: 0 },
            'Non-Averaged': { count: 0, wins: 0, profitSum: 0, dipSum: 0 },
        };
        completedTradeList.forEach((trade) => {
            const key = trade.isAveraged ? 'Averaged' : 'Non-Averaged';
            const profit = getNumeric(trade.netProfitPercentage);
            groups[key].count += 1;
            groups[key].profitSum += profit;
            groups[key].dipSum += getNumeric(trade.lowestPriceDuringTrade ? formatValueWithType(((trade.lowestPriceDuringTrade - trade.buyPrices[0]) / trade.buyPrices[0]) * 100) : 0);
            if (profit >= 0) groups[key].wins += 1;
        });
        const labels = Object.keys(groups);
        return <Bar
            options={{
                responsive: true,
                plugins: { title: { display: true, text: 'Averaging Effectiveness (Avg Return%, Win Rate%, Avg Dip%)' } },
                scales: { y: { title: { display: true, text: 'Percent' } } },
            }}
            data={{
                labels,
                datasets: [
                    {
                        label: 'Avg Return %',
                        data: labels.map((k) => groups[k].count ? formatValueWithType(groups[k].profitSum / groups[k].count) : 0),
                        backgroundColor: 'rgba(54, 162, 235, 0.7)',
                    },
                    {
                        label: 'Win Rate %',
                        data: labels.map((k) => groups[k].count ? formatValueWithType((groups[k].wins / groups[k].count) * 100) : 0),
                        backgroundColor: 'rgba(40, 167, 69, 0.7)',
                    },
                    {
                        label: 'Avg Dip %',
                        data: labels.map((k) => groups[k].count ? formatValueWithType(groups[k].dipSum / groups[k].count) : 0),
                        backgroundColor: 'rgba(220, 53, 69, 0.7)',
                    },
                ],
            }}
        />;
    };

    const getHoldingVsReturnScatter = () => {
        if (!completedTradeList.length) return '';
        return <Scatter
            options={{
                responsive: true,
                plugins: {
                    title: { display: true, text: 'Holding Days vs Final Return % (Correlation View)' },
                    tooltip: {
                        callbacks: {
                            label: (ctx) => `${ctx.raw.symbol}: ${ctx.raw.y}% return at ${ctx.raw.x} days`,
                        },
                    },
                },
                scales: {
                    x: { title: { display: true, text: 'Holding Days' } },
                    y: { title: { display: true, text: 'Final Return %' } },
                },
            }}
            data={{
                datasets: [{
                    label: 'Trades',
                    data: completedTradeList.map((trade) => ({
                        x: getNumeric(trade.holdingDays),
                        y: getNumeric(trade.netProfitPercentage),
                        symbol: company[trade.companyId]?.additionalInfo?.nseCode || company[trade.companyId]?.name || 'UNKNOWN',
                    })),
                    backgroundColor: 'rgba(53, 162, 235, 0.8)',
                }],
            }}
        />;
    };

    const getDipVsReturnScatter = () => {
        if (!completedTradeList.length) return '';
        return <Scatter
            options={{
                responsive: true,
                plugins: {
                    title: { display: true, text: 'Max Dip % vs Final Return % (Recovery Behavior)' },
                    tooltip: {
                        callbacks: {
                            label: (ctx) => `${ctx.raw.symbol}: dip ${ctx.raw.x}% -> return ${ctx.raw.y}%`,
                        },
                    },
                },
                scales: {
                    x: { title: { display: true, text: 'Dip During Trade % (Negative is deeper dip)' } },
                    y: { title: { display: true, text: 'Final Return %' } },
                },
            }}
            data={{
                datasets: [{
                    label: 'Trades',
                    data: completedTradeList.map((trade) => ({
                        x: getNumeric(trade.lowestPriceDuringTrade ? formatValueWithType(((trade.lowestPriceDuringTrade - trade.buyPrices[0]) / trade.buyPrices[0]) * 100) : 0),
                        y: getNumeric(trade.netProfitPercentage),
                        symbol: company[trade.companyId]?.additionalInfo?.nseCode || company[trade.companyId]?.name || 'UNKNOWN',
                    })),
                    backgroundColor: 'rgba(255, 159, 64, 0.8)',
                }],
            }}
        />;
    };

    const getEntryTimeQuality = () => {
        if (!completedTradeList.length) return '';
        const map = {};
        let defaultKey = 'YEAR';
        const mapper = {
            'DAY': {
                format: 'YYYY-MM-DD',
                label: 'Buy Day',
            },
            'MONTH': {
                format: 'MM-YYYY',
                label: 'Buy Month',
            },
            'YEAR': {
                format: 'YYYY',
                label: 'Buy Year',
            },
        }
        const counter = { DAY: 0, MONTH: 0, YEAR: 0 };
        completedTradeList.forEach((trade) => {
            const dayKey = moment(trade.buyDates[0], 'YYYY-MM-DD').isValid() ? moment(trade.buyDates[0], 'YYYY-MM-DD').format(mapper['DAY'].format) : 'Unknown';
            const monthKey = moment(trade.buyDates[0], 'YYYY-MM-DD').isValid() ? moment(trade.buyDates[0], 'YYYY-MM-DD').format(mapper['MONTH'].format) : 'Unknown';
            const yearKey = moment(trade.buyDates[0], 'YYYY-MM-DD').isValid() ? moment(trade.buyDates[0], 'YYYY-MM-DD').format(mapper['YEAR'].format) : 'Unknown';
            if (dayKey !== 'Unknown') counter['DAY'] += 1;
            if (monthKey !== 'Unknown') counter['MONTH'] += 1;
            if (yearKey !== 'Unknown') counter['YEAR'] += 1;
        });

        if (counter['DAY'] <= 30) defaultKey = 'DAY';
        else if (counter['MONTH'] <= 30) defaultKey = 'MONTH';
        else defaultKey = 'YEAR';

        completedTradeList.forEach((trade) => {
            const key = moment(trade.buyDates[0], 'YYYY-MM-DD').isValid() ? moment(trade.buyDates[0], 'YYYY-MM-DD').format(mapper[defaultKey].format) : 'Unknown';
            if (!map[key]) map[key] = { count: 0, wins: 0, sum: 0 };
            map[key].count += 1;
            map[key].sum += getNumeric(trade.netProfitPercentage);
            if (getNumeric(trade.netProfitPercentage) >= 0) map[key].wins += 1;
        });
        const labels = Object.keys(map).sort((a, b) => moment(a, mapper[defaultKey].format) - moment(b, mapper[defaultKey].format));
        return <Bar
            options={{
                responsive: true,
                plugins: { title: { display: true, text: `Entry Time Quality by ${mapper[defaultKey].label} (Avg Return% and Win Rate%)` } },
                scales: {
                    y: { title: { display: true, text: 'Avg Return %' } },
                    y1: { position: 'right', grid: { drawOnChartArea: false }, title: { display: true, text: 'Win Rate %' } },
                },
            }}
            data={{
                labels,
                datasets: [
                    { label: 'Avg Return %', data: labels.map((k) => formatValueWithType(map[k].sum / map[k].count)), backgroundColor: 'rgba(153, 102, 255, 0.7)', yAxisID: 'y' },
                    { label: 'Win Rate %', data: labels.map((k) => formatValueWithType((map[k].wins / map[k].count) * 100)), backgroundColor: 'rgba(40, 167, 69, 0.7)', yAxisID: 'y1' },
                ],
            }}
        />;
    };

    const getPnlDistribution = () => {
        if (!completedTradeList.length) return '';
        const binSize = 5;
        const profits = completedTradeList.map((trade) => getNumeric(trade.netProfitPercentage));
        const minVal = Math.floor(Math.min(...profits) / binSize) * binSize;
        const maxVal = Math.ceil(Math.max(...profits) / binSize) * binSize;
        const bins = [];
        for (let start = minVal; start < maxVal; start += binSize) {
            bins.push({ start, end: start + binSize, count: 0 });
        }
        profits.forEach((value) => {
            const idx = Math.min(Math.floor((value - minVal) / binSize), bins.length - 1);
            if (idx >= 0 && bins[idx]) bins[idx].count += 1;
        });
        return <Bar
            options={{
                responsive: true,
                plugins: { title: { display: true, text: 'P&L Distribution Histogram (Completed Trades)' } },
                scales: { x: { title: { display: true, text: 'Profit % Range' } }, y: { title: { display: true, text: 'Trade Count' } } },
            }}
            data={{
                labels: bins.map((b) => `${b.start} to ${b.end}%`),
                datasets: [{ label: 'Trade Count', data: bins.map((b) => b.count), backgroundColor: 'rgba(75, 192, 192, 0.7)' }],
            }}
        />;
    };

    const getCapitalConcentration = () => {
        if (!detailedTradeList.length) return '';
        const map = {};
        detailedTradeList.forEach((trade) => {
            const key = company[trade.companyId]?.additionalInfo?.nseCode || company[trade.companyId]?.additionalInfo?.nseCode || company[trade.companyId]?.name || 'UNKNOWN';
            let invested = 0;
            if (Array.isArray(trade.buyPrices) && Array.isArray(trade.buyQties) && trade.buyPrices.length === trade.buyQties.length) {
                invested = trade.buyPrices.reduce((sum, price, idx) => sum + (getNumeric(price) * getNumeric(trade.buyQties[idx])), 0);
            } else {
                invested = getNumeric(trade.avgBuyPrice) * Math.max(getNumeric(trade.quantity, 1), 1);
            }
            map[key] = (map[key] || 0) + invested;
        });
        const sorted = Object.entries(map).sort((a, b) => b[1] - a[1]);
        const top = sorted.slice(0, 20);
        const others = sorted.slice(20).reduce((sum, [, value]) => sum + value, 0);
        const labels = top.map(([k]) => k);
        const values = top.map(([, v]) => formatValueWithType(v));
        if (others > 0) {
            labels.push('Others');
            values.push(formatValueWithType(others));
        }
        const backgroundColors = labels.map((_, index) => `hsl(${Math.round((index * 360) / Math.max(labels.length, 1))}, 65%, 55%)`);
        return <Pie
            options={{
                responsive: true,
                plugins: {
                    title: { display: true, text: 'Capital Concentration by Symbol (Invested Amount)' },
                    tooltip: {
                        callbacks: {
                            label: (ctx) => `${ctx.label}: ${ctx.raw}`,
                        },
                    },
                },
            }}
            data={{ labels, datasets: [{ data: values, backgroundColor: backgroundColors }] }}
        />;
    };

    return (
        <div className="row">
            <div className="col-md-3">
                {renderChartCard(<Pie data={pieData} />)}
            </div>
            <div className="col-md-12">
                {renderChartCard(getHoldingDaysGrouped())}
            </div>
            <div className="col-md-12">
                {renderChartCard(getHoldingDays())}
            </div>
            <div className="col-md-12">
                {renderChartCard(getProfitTrend())}
            </div>
            <div className="col-md-12">
                {renderChartCard(getQuantityTrend())}
            </div>
            <div className="col-md-12">
                {renderChartCard(getDipDuringTrade())}
            </div>
            <div className="col-md-4">
                {renderChartCard(getAveragedData('SUCCESS'))}
            </div>
            <div className="col-md-4">
                {renderChartCard(getAveragedData('FAILURE'))}
            </div>
            <div className="col-md-4">
                {renderChartCard(getAveragedData('IN_COMPLETE'))}
            </div>

            <div className="col-md-12">
                {renderChartCard(getGroupByDate('YEAR'))}
            </div>
            <div className="col-md-12">
                {renderChartCard(getGroupByDate('MONTH'))}
            </div>
            <div className="col-md-12">
                {renderChartCard(getInCompleteGroupByDate('DAY', 'Holdings Trades by', maxHoldingByDay, 'Max Holding Trade Qty'))}
            </div>


            <div className="col-md-12">
                {renderChartCard(getInCompleteGroupByDate('DAY', 'Amount Holding by', maxHoldingAmountByDay, 'Max Holding Trade Amount'))}
            </div>

            <div className="col-md-12">
                {renderChartCard(getOpenTrades())}
            </div>

            <div className="col-md-12">
                {renderChartCard(getSectorIndustryPerformance())}
            </div>

            <div className="col-md-12">
                {renderChartCard(getSymbolWiseWinRate())}
            </div>

            <div className="col-md-12">
                {renderChartCard(getStopLossImpact())}
            </div>

            <div className="col-md-12">
                {renderChartCard(getAveragingEffectiveness())}
            </div>

            <div className="col-md-12">
                {renderChartCard(getHoldingVsReturnScatter())}
            </div>

            <div className="col-md-12">
                {renderChartCard(getDipVsReturnScatter())}
            </div>

            <div className="col-md-12">
                {renderChartCard(getEntryTimeQuality())}
            </div>

            <div className="col-md-12">
                {renderChartCard(getPnlDistribution())}
            </div>

            <div className="col-md-12">
                {renderChartCard(getCapitalConcentration())}
            </div>

            <div className="col-md-12">
                {renderChartCard(getCapitalGrowth())}
            </div>

            {/* <div className="col-md-6">
                <Bar data={barData} />
            </div> */}
        </div>
    );
};

export default Charts;
